import type { Theme, ThemePreference } from '@/store/features/theme.feature';
import { createContext, useContext } from 'react';
import type { AppearanceLook } from './appearance.util';

export interface AppearanceContextValue {
  /** The saved appearance is back and changes are taken. */
  ready: boolean;
  /** The saved appearance could not be read. */
  failed: boolean;
  /** Reads the saved appearance again. */
  retry: () => void;
  /** The mode the user chose. */
  preference: ThemePreference;
  /** The mode on screen. */
  mode: Theme;
  /** The look on screen, the default one with nothing picked. */
  look: AppearanceLook;
  /** True while the look is the default one. */
  isDefault: boolean;
  setPreference: (preference: ThemePreference) => void;
  /**
   * Paints a look without saving it, once per animation frame; null paints
   * the saved look again.
   */
  preview: (look: AppearanceLook | null) => void;
  /** Shows and saves a look. */
  commit: (look: AppearanceLook) => void;
  /** Back to today's chrome, with Undo on a toast. */
  reset: () => void;
}

export const AppearanceContext = createContext<AppearanceContextValue | null>(
  null
);

/** The appearance, or null outside the provider. */
export function useAppearance(): AppearanceContextValue | null {
  return useContext(AppearanceContext);
}
